import React, { useState, useContext } from "react";
import styled from "styled-components";
import {
  Form,
  Input,
  Button,
  message,
  Card,
  Row,
  Col,
  Divider,
  Modal,
  Checkbox,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  DollarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  StarOutlined,
  CalendarOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../helpers/mixpanel";
import { APP_NAME, TAGLINE_SENTENCE } from "../helpers/constants";
import SEO from "../components/SEO";
import { device } from "../lib/breakpoints";

export default function Agents() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Track agent signup
      trackEvent("Agent Signup", {
        name: values.name,
        email: values.email,
        phone: values.phone,
        brokerage: values.brokerage,
        licenseNumber: values.licenseNumber,
        experience: values.experience,
      });

      // TODO: Implement backend API call to create agent account
      console.log("Agent signup data:", values);

      message.success(
        "Thank you for your interest! We'll contact you within 24 hours with your agent discount code and referral program details."
      );
      form.resetFields();
      setShowSignupModal(false);
    } catch (error) {
      console.error("Agent signup error:", error);
      message.error(
        "Something went wrong. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUserClick = () => {
    if (user) {
      navigate("/profile");
    } else {
      navigate("/signup");
    }
  };

  return (
    <Container>
      <SEO
        title={`Real Estate Agent Partnership | ${APP_NAME} - Earn Commissions & Get Discounts`}
        description={`Partner with ${APP_NAME}. ${TAGLINE_SENTENCE} Get exclusive discounts for your clients and earn commissions for successful referrals. Join our agent network today.`}
        keywords={`real estate agent partnership, property management agent program, landlord software agent discount, real estate agent referral program, property management commissions`}
        imageAlt={`${APP_NAME} real estate agent partnership program`}
        category="Partnership"
        tags={[
          "real estate agent partnership",
          "property management agent program",
          "landlord software agent discount",
          "real estate agent referral program",
        ]}
      />

      <HeroSection>
        <HeroContent>
          <HeroTitle>Partner with {APP_NAME} as a Real Estate Agent</HeroTitle>
          <HeroSubtitle>
            Get exclusive benefits for your clients and earn commissions for
            every successful referral. Join our growing network of real estate
            professionals.
          </HeroSubtitle>
          <HeroStats>
            <StatItem>
              <StatNumber>1</StatNumber>
              <StatLabel>Month Free Trial</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>$25</StatNumber>
              <StatLabel>Per Referral Reward</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>500+</StatNumber>
              <StatLabel>Active Agents</StatLabel>
            </StatItem>
          </HeroStats>
          <HeroCTA>
            <HeroButton onClick={() => setShowSignupModal(true)}>
              <RocketOutlined />
              Join Agent Network
            </HeroButton>
          </HeroCTA>
        </HeroContent>
      </HeroSection>

      <BenefitsSection>
        <SectionTitle>Why Partner with {APP_NAME}?</SectionTitle>
        <BenefitsGrid>
          <BenefitCard>
            <BenefitIcon>
              <DollarOutlined />
            </BenefitIcon>
            <BenefitTitle>Earn $25 Per Referral</BenefitTitle>
            <BenefitDescription>
              Earn $25 for every client who signs up and pays for their first
              month. Track your referrals and rewards in real-time.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <CalendarOutlined />
            </BenefitIcon>
            <BenefitTitle>1 Month Free</BenefitTitle>
            <BenefitDescription>
              Your clients get our standard 1-month free trial to get started
              before their first charge.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <TeamOutlined />
            </BenefitIcon>
            <BenefitTitle>Dedicated Support</BenefitTitle>
            <BenefitDescription>
              Get priority support and dedicated account management. We're here
              to help you succeed.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <StarOutlined />
            </BenefitIcon>
            <BenefitTitle>Marketing Materials</BenefitTitle>
            <BenefitDescription>
              Access co-branded marketing materials, case studies, and
              presentation decks to help you sell.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <RocketOutlined />
            </BenefitIcon>
            <BenefitTitle>Quick Setup</BenefitTitle>
            <BenefitDescription>
              Get started in minutes with your unique referral link and
              personalized dashboard to track your success.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <CheckCircleOutlined />
            </BenefitIcon>
            <BenefitTitle>No Minimums</BenefitTitle>
            <BenefitDescription>
              No quotas or minimum requirements. Refer as many or as few clients
              as you'd like - every referral counts.
            </BenefitDescription>
          </BenefitCard>
        </BenefitsGrid>
      </BenefitsSection>

      <RewardHighlightSection>
        <RewardContainer>
          <RewardBadge>
            <RewardIcon>
              <DollarOutlined />
            </RewardIcon>
            <RewardText>
              <RewardAmount>$25</RewardAmount>
              <RewardLabel>Per Successful Referral</RewardLabel>
            </RewardText>
          </RewardBadge>
          <RewardDescription>
            Earn $25 for every client who signs up and pays for their first
            month. No minimums, no quotas - just rewards for helping your
            clients succeed.
          </RewardDescription>
          <RewardCTA>
            <RewardButton onClick={() => setShowSignupModal(true)}>
              Start Earning Today
            </RewardButton>
          </RewardCTA>
        </RewardContainer>
      </RewardHighlightSection>

      <SignupSection>
        <SignupContainer>
          <SignupTitle>Ready to Start Earning?</SignupTitle>
          <SignupSubtitle>
            Join our agent network and start earning $25 per referral while
            giving your clients a 1-month free trial.
          </SignupSubtitle>

          <SignupCTA>
            <SignupButton onClick={() => setShowSignupModal(true)}>
              <RocketOutlined />
              Join Agent Network
            </SignupButton>
          </SignupCTA>

          <Divider>Already a {APP_NAME} user?</Divider>

          <ExistingUserButton onClick={handleExistingUserClick}>
            Access Your Account
          </ExistingUserButton>
        </SignupContainer>
      </SignupSection>

      <FAQSection>
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        <FAQGrid>
          <FAQItem>
            <FAQQuestion>How much do I earn per referral?</FAQQuestion>
            <FAQAnswer>
              You earn $25 for every client who signs up using your referral
              link and pays for their first month. Rewards are paid within 30
              days of the client's first payment.
            </FAQAnswer>
          </FAQItem>
          <FAQItem>
            <FAQQuestion>What benefits do my clients get?</FAQQuestion>
            <FAQAnswer>
              Your clients get our standard 1-month free trial before their
              first charge.
            </FAQAnswer>
          </FAQItem>
          <FAQItem>
            <FAQQuestion>How do I track my referrals?</FAQQuestion>
            <FAQAnswer>
              Once approved, you'll receive a unique referral link and access to
              a dashboard where you can track all your referrals and commission
              earnings in real-time.
            </FAQAnswer>
          </FAQItem>
          <FAQItem>
            <FAQQuestion>Is there a minimum requirement?</FAQQuestion>
            <FAQAnswer>
              No minimum requirements! You can refer as many or as few clients
              as you'd like. There's no pressure or quotas to meet.
            </FAQAnswer>
          </FAQItem>
        </FAQGrid>
      </FAQSection>

      {/* Agent Signup Modal */}
      <StyledModal
        title={
          <ModalTitle>
            <RocketOutlined />
            Join Our Agent Network
          </ModalTitle>
        }
        open={showSignupModal}
        onCancel={() => setShowSignupModal(false)}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        <ModalContent>
          <ModalSubtitle>
            Fill out the form below and we'll contact you within 24 hours with
            your agent discount code and referral program details.
          </ModalSubtitle>

          <StyledForm
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
          >
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Full Name"
                  rules={[
                    { required: true, message: "Please enter your full name" },
                  ]}
                >
                  <StyledInput
                    prefix={<UserOutlined />}
                    placeholder="Enter your full name"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <StyledInput
                    prefix={<MailOutlined />}
                    placeholder="Enter your email address"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your phone number",
                    },
                  ]}
                >
                  <StyledInput
                    prefix={<PhoneOutlined />}
                    placeholder="Enter your phone number"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="brokerage"
                  label="Brokerage/Company"
                  rules={[
                    { required: true, message: "Please enter your brokerage" },
                  ]}
                >
                  <StyledInput
                    prefix={<BankOutlined />}
                    placeholder="Enter your brokerage name"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="licenseNumber"
                  label="Real Estate License Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your license number",
                    },
                  ]}
                >
                  <StyledInput placeholder="Enter your license number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="experience"
                  label="Years of Experience"
                  rules={[
                    {
                      required: true,
                      message: "Please select your experience level",
                    },
                  ]}
                >
                  <StyledInput placeholder="e.g., 5 years" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="agreeToTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            "You must agree to the Terms of Use and Privacy Policy"
                          )
                        ),
                },
              ]}
            >
              <TermsCheckbox>
                I agree to the{" "}
                <TermsLink href="/terms" target="_blank">
                  Terms of Use
                </TermsLink>{" "}
                and{" "}
                <TermsLink href="/privacy" target="_blank">
                  Privacy Policy
                </TermsLink>
                , which govern my participation in the Clumse Agent
                Referral Program, including commission structure, payment terms,
                and program requirements.
              </TermsCheckbox>
            </Form.Item>

            <Form.Item>
              <ModalSubmitButton
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
              >
                Join Agent Network
              </ModalSubmitButton>
            </Form.Item>
          </StyledForm>
        </ModalContent>
      </StyledModal>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  background: var(--color-background);
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5f2d 0%, #4a7c59 100%);
  padding: 6rem 2rem;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: 1;
  }

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.375rem;
  line-height: 1.6;
  margin-bottom: 3rem;
  opacity: 0.95;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 1.25rem;
  }
`;

const HeroStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 4rem;
  margin-top: 3rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #fff;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1.125rem;
  opacity: 0.9;
  font-family: var(--font-secondary);
`;

const HeroCTA = styled.div`
  margin-top: 3rem;
`;

const HeroButton = styled(Button)`
  height: 60px;
  padding: 0 3rem;
  font-size: 1.25rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 16px;
  font-family: var(--font-secondary);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  .anticon {
    margin-right: 0.75rem;
    font-size: 1.125rem;
  }
`;

const BenefitsSection = styled.section`
  padding: 6rem 2rem;
  background: var(--color-bg-secondary);

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 4rem;
  color: var(--color-text);
  font-family: var(--font-primary);
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const BenefitCard = styled.div`
  background: var(--color-background);
  padding: 3rem 2.5rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px var(--shadow-light);
  border: 1px solid var(--color-border-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15);
  }

  @media ${device.mobile} {
    padding: 2.5rem 2rem;
  }
`;

const BenefitIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 2rem;
  color: var(--color-primary);
`;

const BenefitTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--color-text);
  font-family: var(--font-primary);
`;

const BenefitDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const SignupSection = styled.section`
  padding: 6rem 2rem;
  background: var(--color-background);

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const SignupContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SignupTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--color-text);
  font-family: var(--font-primary);
`;

const SignupSubtitle = styled.p`
  font-size: 1.25rem;
  text-align: center;
  margin-bottom: 3rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const SignupCTA = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SignupButton = styled(Button)`
  height: 60px;
  padding: 0 3rem;
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  border: none;
  border-radius: 16px;
  font-family: var(--font-secondary);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25);
  }

  .anticon {
    margin-right: 0.75rem;
    font-size: 1.125rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 1.125rem;
  font-weight: 600;
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  border: none;
  border-radius: 12px;
  font-family: var(--font-secondary);

  &:hover {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25);
  }
`;

const ExistingUserButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 1.125rem;
  font-weight: 600;
  background: transparent;
  border: 2px solid var(--color-primary);
  color: var(--color-primary);
  border-radius: 12px;
  font-family: var(--font-secondary);

  &:hover {
    background: var(--color-primary);
    color: white;
    transform: translateY(-2px);
  }
`;

const FAQSection = styled.section`
  padding: 6rem 2rem;
  background: var(--color-bg-secondary);

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FAQItem = styled.div`
  background: var(--color-background);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px var(--shadow-light);
  border: 1px solid var(--color-border-light);
`;

const FAQQuestion = styled.h3`
  font-size: 1.375rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-text);
  font-family: var(--font-primary);
`;

const FAQAnswer = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const RewardHighlightSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #2c5f2d 0%, #4a7c59 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.05);
    z-index: 1;
  }

  @media ${device.mobile} {
    padding: 3rem 1.5rem;
  }
`;

const RewardContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const RewardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 2rem 3rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 2rem;
  }
`;

const RewardIcon = styled.div`
  font-size: 3rem;
  color: #fff;
`;

const RewardText = styled.div`
  text-align: center;
`;

const RewardAmount = styled.div`
  font-size: 4rem;
  font-weight: 700;
  color: #fff;
  font-family: var(--font-primary);
  line-height: 1;
  margin-bottom: 0.5rem;

  @media ${device.mobile} {
    font-size: 3rem;
  }
`;

const RewardLabel = styled.div`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: var(--font-secondary);
  font-weight: 500;
`;

const RewardDescription = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  font-family: var(--font-secondary);
  max-width: 600px;
  margin: 0 auto;

  @media ${device.mobile} {
    font-size: 1.125rem;
  }
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  font-family: var(--font-primary);

  .anticon {
    color: var(--color-primary);
    font-size: 1.25rem;
  }
`;

const ModalContent = styled.div`
  padding: 1rem 0;
`;

const ModalSubtitle = styled.p`
  font-size: 1rem;
  color: var(--color-text-light);
  text-align: center;
  margin-bottom: 2rem;
  font-family: var(--font-secondary);
  line-height: 1.6;
`;

const ModalSubmitButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 1.125rem;
  font-weight: 600;
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  border: none;
  border-radius: 12px;
  font-family: var(--font-secondary);

  &:hover {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25);
  }
`;

const TermsCheckbox = styled(Checkbox)`
  .ant-checkbox-wrapper {
    font-family: var(--font-secondary);
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--color-text-light);
  }

  .ant-checkbox {
    margin-top: 0.125rem;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: var(--color-primary);
  }
`;

const TermsLink = styled.a`
  color: var(--color-primary);
  text-decoration: underline;
  font-weight: 600;

  &:hover {
    color: var(--color-primary-dark);
    text-decoration: underline;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border: none;
    overflow: hidden;
  }

  .ant-modal-header {
    background: linear-gradient(135deg, #2c5f2d 0%, #4a7c59 100%);
    border-bottom: none;
    padding: 2rem 2rem 1.5rem;
    margin: 0;
  }

  .ant-modal-title {
    color: white;
    font-family: var(--font-primary);
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
  }

  .ant-modal-close {
    color: white;
    top: 1.5rem;
    right: 1.5rem;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .ant-modal-close-x {
    width: 32px;
    height: 32px;
    line-height: 32px;
    font-size: 18px;
  }

  .ant-modal-body {
    padding: 2rem;
    background: var(--color-background);
    border: none;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 1.5rem;
  }

  .ant-form-item-label > label {
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: var(--color-text) !important;
    margin-bottom: 0.5rem !important;
    display: block !important;
  }

  .ant-form-item-explain-error {
    font-family: var(--font-secondary) !important;
    font-size: 0.75rem !important;
    color: #ff4d4f !important;
    margin-top: 0.25rem !important;
    line-height: 1.4 !important;
  }

  .ant-form-item-explain {
    font-family: var(--font-secondary) !important;
    font-size: 0.75rem !important;
    color: var(--color-text-light) !important;
    margin-top: 0.25rem !important;
    line-height: 1.4 !important;
  }
`;

const StyledInput = styled(Input)`
  &.ant-input {
    height: 48px;
    border: 2px solid var(--color-border-medium);
    border-radius: 12px;
    padding: 0 12px;
    font-family: var(--font-secondary);
    font-size: 1rem;
    background: var(--color-bg-secondary);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: var(--color-primary);
    }

    &:focus,
    &.ant-input-focused {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
    }

    &::placeholder {
      color: var(--color-text-muted);
      font-family: var(--font-secondary);
    }
  }

  &.ant-input-affix-wrapper {
    height: 48px;
    border: 2px solid var(--color-border-medium);
    border-radius: 12px;
    background: var(--color-bg-secondary);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: var(--color-primary);
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
    }

    .ant-input {
      background: transparent;
      border: none;
      box-shadow: none;
      font-family: var(--font-secondary);
      font-size: 1rem;

      &::placeholder {
        color: var(--color-text-muted);
        font-family: var(--font-secondary);
      }
    }

    .ant-input-prefix {
      color: var(--color-text-muted);
      margin-right: 8px;
    }
  }
`;

const RewardCTA = styled.div`
  margin-top: 2rem;
`;

const RewardButton = styled(Button)`
  height: 50px;
  padding: 0 2.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 12px;
  font-family: var(--font-secondary);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }
`;
